import { useEffect } from "react";
import { Link } from "react-router-dom";
import { LogoMark } from "@/components/site/Logo";

export const COMPANY = {
  name: "ArcaCentum, Inc.",
  address: ["2654 W Horizon Ridge Pkwy", "Ste B5 #2338", "Henderson, NV 89052", "United States"],
  phone: "(888) 977-5190",
  phoneHref: "tel:+18889775190",
  email: "support@arcacentum.com",
  site: "https://arcacentum.com",
};

export const LEGAL_LINKS = [
  { label: "Privacy Policy", to: "/privacy" },
  { label: "Terms of Service", to: "/terms" },
  { label: "Refund Policy", to: "/refund-policy" },
];

export const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="font-display text-xl sm:text-2xl font-600 text-white tracking-[-0.01em]">{title}</h2>
    <div className="mt-3 space-y-3 text-[15px] leading-relaxed text-white/65 font-light">{children}</div>
  </section>
);

export const Item = ({ label, children }) => (
  <li className="pl-1">
    {label && <span className="text-white/85 font-normal">{label} </span>}
    {children}
  </li>
);

export const UL = ({ children }) => <ul className="list-disc pl-5 space-y-2">{children}</ul>;

export default function LegalLayout({ title, updated, intro, children, testid }) {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `${title} — ArcaCentum, Inc.`;
    return () => { document.title = "ArcaCentum — AI Technology & Software Products"; };
  }, [title]);

  return (
    <div data-testid={testid} className="min-h-screen bg-[#0a0a0f] text-white">
      <header className="border-b border-white/[0.06]">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 py-5 flex items-center justify-between">
          <Link to="/" data-testid="legal-home-link" className="flex items-center gap-2.5">
            <LogoMark size={26} />
            <span className="font-display text-base font-600 text-white">ArcaCentum</span>
          </Link>
          <nav className="hidden sm:flex gap-5">
            {LEGAL_LINKS.map((l) => (
              <Link key={l.to} to={l.to} className="text-xs text-white/50 hover:text-white transition-colors">{l.label}</Link>
            ))}
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 sm:px-8 py-14 sm:py-20">
        <p className="text-xs uppercase tracking-[0.25em] text-cyan-300/80">Legal</p>
        <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-600 tracking-[-0.02em]">{title}</h1>
        <p className="mt-3 text-sm text-white/40">Last updated: {updated}</p>
        {intro && <p className="mt-6 text-[15px] leading-relaxed text-white/65 font-light">{intro}</p>}
        {children}

        <div className="mt-14 rounded-2xl border border-white/10 bg-white/[0.03] p-6">
          <h3 className="font-display text-lg font-600 text-white">Contact us</h3>
          <p className="mt-2 text-sm text-white/60 font-light">
            {COMPANY.name}
            <br />{COMPANY.address[0]}
            <br />{COMPANY.address[1]}
            <br />{COMPANY.address[2]}, {COMPANY.address[3]}
            <br />Phone: <a href={COMPANY.phoneHref} className="text-cyan-300 hover:text-cyan-200">{COMPANY.phone}</a>
            <br />Email: <a href={`mailto:${COMPANY.email}`} className="text-cyan-300 hover:text-cyan-200">{COMPANY.email}</a>
          </p>
        </div>
      </main>

      <footer className="border-t border-white/[0.06] py-8">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/30">
          <span>© {new Date().getFullYear()} {COMPANY.name} All rights reserved.</span>
          <div className="flex gap-4">
            {LEGAL_LINKS.map((l) => (
              <Link key={l.to} to={l.to} className="hover:text-white/60 transition-colors">{l.label}</Link>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
